/* eslint-disable prettier/prettier */
import { FC, useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Dialog } from 'primereact/dialog';
import { Messages } from 'primereact/messages';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faSave } from '@fortawesome/free-solid-svg-icons';

import { DocumentoSeleccionado } from '@/types/getPendingPaymentsType';
import { useMessage } from '@/hooks';
import { queryClient } from '@/providers/AppProvider';

import { Button, InputTextField, LoadingSpinner } from '@/components';

import { saveSelection } from '../services/saveSelection';

type CreateSelectionModalProps = {
  selection: DocumentoSeleccionado[];
  onClose: (res?: boolean) => void;
  opened: boolean;
  codigo: string;
  retencionTitular: boolean;
  vencimiento: Date;
  titular: {
    razonSocial: string;
    ruc: string;
  } | null;
};

const CreateSelectionModal: FC<CreateSelectionModalProps> = ({
  selection = [],
  onClose = () => {},
  opened = false,
  codigo,
  retencionTitular,
  vencimiento,
  titular,
}) => {
  const { t } = useTranslation('accountStatement');
  const [value, setValue] = useState('');
  const { showMessage, elementRef, clearMessages } = useMessage();
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const showMessageError = useCallback(
    (message: string, title?: string) => {
      showMessage({
        severity: 'error',
        detail: message,
        summary: title,
      });
    },
    [showMessage]
  );

  const { mutate: save, isPending } = useMutation({
    mutationFn: saveSelection,
    onSuccess: (res) => {
      // eslint-disable-next-line no-console
      console.log(res);
      onClose(true);
      queryClient.invalidateQueries({
        queryKey: ['accountStatementSelections'],
      });
    },
    onError: (error) => {
      const items = (error?.cause as string)?.split(',') || [1];
      items.forEach((item) => {
        showMessageError(t(error?.message, { nroDoca: item }));
      });
    },
  });

  useEffect(() => {
    setTimeout(() => {
      clearMessages();
      const itemsHasSelection = selection.filter((item) => !!item?.seleccion);

      itemsHasSelection.forEach(({ nroDoca, seleccion }) => {
        showMessageError(
          t('itemHasSelectionError', { nroDoca, selection: seleccion }),
          t('Error')
        );
      });

      setButtonDisabled(!selection.length || !!itemsHasSelection.length);
    }, 100);
  }, [selection, showMessageError, t, clearMessages]);

  const onSaveButtonClick = async () => {
    if (!titular) return;

    clearMessages();

    const { razonSocial, ruc: rut } = titular;
    const body = {
      documents: selection,
      rut,
      razonSocial,
      referencia: value,
      codCliente: Number(codigo),
      vencimiento,
      retencionTitular,
    };
    save(body);
  };

  const dialogHeader = (
    <div className="text-blue-800">
      <FontAwesomeIcon
        icon={faClipboardCheck}
        style={{ marginRight: '0.5rem' }}
      />
      {t('createSelection')}
    </div>
  );

  const onInputChange = (e: { target: { value: string } }) => {
    setValue(e.target.value);
  };

  const loading = isPending;

  return (
    <Dialog
      header={dialogHeader}
      visible={opened}
      style={{ width: '480px' }}
      onHide={() => onClose(false)}
      modal
      breakpoints={{ '500px': '95vw' }}
      footer={
        <div className="w-full flex justify-content-end gap-1">
          <Button
            className="w-auto px-3"
            label={t('cancel')}
            onClick={() => onClose(false)}
            style={{ color: '#170a33' }}
            text
          />
          <Button
            disabled={isPending || !titular || buttonDisabled}
            className="w-auto px-3"
            onClick={onSaveButtonClick}
            green
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            {t('create')}
          </Button>
        </div>
      }
    >
      {loading && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}

      <div className="w-full p-3">
        <p className="text-sm text-gray-900">{t('createSelectionMessage')}</p>
        <InputTextField
          className="col-10 md:col-5 pl-0 w-full"
          placeholder={t('createSelectionPlaceholder')}
          id="container"
          onChange={onInputChange}
          value={value}
          name="reference"
          label={t('table.reference')}
        />
        <Messages ref={elementRef} className="w-full" />
      </div>
    </Dialog>
  );
};

export default CreateSelectionModal;
